import { createAction, createActions } from 'redux-actions'
import cloneDeep from 'lodash/cloneDeep'
import { browserName } from 'react-device-detect'

import { PROFILE_SAVED } from 'app/constants/toastrMessages'
import { PROFILE_FORM_NAME } from 'app/constants/profile'
import { COMMIT, DID, DIDNOT } from 'app/constants/identityActions'
import { createRequestActions as request } from 'store/actionCreators'
import * as types from './actionTypes'
import { selectLoginErrorMessage } from 'helpers/parseSubmissionErrors'
import { parsePhoneBeforeSend } from 'helpers/parsePhone'
import { parseNotificationTypesBeforeSend } from 'helpers/notificationType'
import { ISO_CODE } from 'app/constants/phoneCountryCodes'
import { convertDateToUTC } from 'helpers/timeConvertion'

export const switchRole = createAction('IDENTITY/SWITCH_ROLE', roleIndex => ({ roleIndex }))

export const changeIdentityRole = createAction('IDENTITY/CHANGE_ROLE', roleIndex => ({ roleIndex }))

/**
 * Create actions:
 * identity.postAuthToken.request, identity.postAuthToken.success, identity.postAuthToken.error
 * identity.postAuthEmail.request, identity.postAuthEmail.success, identity.postAuthEmail.error
 */

export const { identity } = createActions({
  IDENTITY: {
    SAVE_TIME_PREFERENCES: timePreferences => ({ timePreferences }),
  },
})

export const postAuthEmail = request('IDENTITY/AUTH_EMAIL_REQUEST', email => ({
  request: {
    url: '/auth/email',
    method: 'POST',
    data: {
      email,
    },
  },
}))

export const postAuthPhone = request('IDENTITY/AUTH_PHONE_REQUEST', phone => ({
  request: {
    url: '/auth/phone',
    method: 'POST',
    data: {
      phone,
    },
  },
}))

export const postInvite = request('IDENTITY/POST_INVITE_REQUEST', data => ({
  request: {
    url: `organizations/${data.organizationId}/users/self-invite`,
    method: 'POST',
    data,
  },
  success: 'You have been successfully invited',
}))

export const clearAuthStatuses = createAction('IDENTITY/AUTH_STATUSES_CLEAR')

export const subscribeToProTrack = request('IDENTITY/SUBSCRIBE_TO_PROTRACK', data => ({
  request: {
    url: '/protrack-levels-progress/subscribe',
    method: 'POST',
    data: data.toJS ? data.toJS() : data,
  },
}))

export const silentSubscribeToProTrack = request('IDENTITY/SILENT_SUBSCRIBE_TO_PROTRACK', data => ({
  request: {
    url: '/protrack-levels-progress/subscribe',
    method: 'POST',
    data: data.toJS ? data.toJS() : data,
  },
}))

export const updateSubscribeToProTrack = request('IDENTITY/UPDATE_SUBSCRIBE_TO_PROTRACK', data => ({
  request: {
    url: '/protrack-levels-progress/subscribe',
    method: 'PUT',
    data: data.toJS ? data.toJS() : data,
  },
}))

/**
 * Handled also by saga @see store/entities/identity/sagas/unsubscribeProgress.js
 */
export const unsubscribeProgress = request('IDENTITY_SUBSCRIBE/UNSUBSCRIBE', ({ data, formName }) => ({
  request: {
    url: '/protrack-levels-progress/unsubscribe',
    method: 'POST',
    data: data.toJS(),
  },
  formName,
}))

export const uploadAvatarRequest = (file, mime, ext, formName) => ({
  type: types.IDENTITY_UPLOAD_AVATAR_REQUEST,
  payload: {
    file,
    ext,
    mime,
    formName,
  },
})

export const saveProfile = request('IDENTITY/SAVE_PROFILE', data => {
  let profile = data.toJS ? data.toJS() : cloneDeep(data)
  /** TODO: should be handle on backend side */
  if (profile.dayoffs) {
    profile.dayoffs = profile.dayoffs.filter(el => el.startDate && el.endDate)
    profile.dayoffs = convertDateToUTC(profile.dayoffs)
  }
  profile.browser = browserName
  profile.notificationType = parseNotificationTypesBeforeSend(profile.notificationType)

  if (profile.phone && profile.phoneISO) {
    profile.phone = parsePhoneBeforeSend(`${ISO_CODE[profile.phoneISO]}${profile.phone}`)
  } else {
    delete profile.phone
  }

  // remove then backend fixed name commitedNotificationTime
  profile.commitedNotificationTime = profile.committedNotificationTime

  return {
    request: {
      url: '/me',
      method: 'PUT',
      data: profile,
    },
    success: PROFILE_SAVED,
    form: profile.form || PROFILE_FORM_NAME,
  }
})

export const getProfile = request('IDENTITY/GET_PROFILE', () => ({
  request: {
    url: '/me',
  },
}))

export const logoutRequest = () => ({
  type: types.IDENTITY_LOGOUT_REQUEST,
})

export const logoutSuccess = () => ({
  type: types.IDENTITY_LOGOUT_SUCCESS,
})

export const forceLogoutRequest = () => ({
  type: types.IDENTITY_FORCE_LOGOUT_REQUEST,
})

export const forceLogoutSuccess = () => ({
  type: types.IDENTITY_FORCE_LOGOUT_SUCCESS,
})

/**
 * getIdentityProgress() - executing action
 * getIdentityProgress.toString() - request Action Type
 * getIdentityProgress.success.toString() - success Action Type
 * getIdentityProgress.fail.toString() - fail Action Type
 */
export const getIdentityProgress = request('IDENTITY/GET_PROGRESS', () => ({
  request: {
    url: '/protrack-levels-progress',
  },
}))

export const AUTH_URL = '/auth/login'
export const authLogin = request('IDENTITY/LOGIN_PROCESS', ({ data, flagForErrorMessage }) => ({
  request: {
    url: AUTH_URL,
    method: 'POST',
    data,
  },
  fail: selectLoginErrorMessage(flagForErrorMessage),
}))

export const processEmailQuery = createAction('IDENTITY/PROCESS_EMAIL_QUERY')

const activityActionCreator = actionType => (data = {}) => {
  data = data && data.toJS ? data.toJS() : data
  return {
    request: {
      url: '/protrack-levels-progress',
      method: 'POST',
      data: { actionType, actionSource: 'web', ...data },
    },
  }
}

export const activityActions = {
  commit: request('IDENTITY/ACTIVITY_ACTIONS/COMMIT', activityActionCreator(COMMIT)),
  did: request('IDENTITY/ACTIVITY_ACTIONS/DID', activityActionCreator(DID)),
  didnot: request('IDENTITY/ACTIVITY_ACTIONS/DID_NOT', activityActionCreator(DIDNOT)),
  // TODO: maybe should be moved to separate action due to it not performed by user directly
  // TODO: perhaps can be removed at all, because do not used by code???
  complete: request('IDENTITY/ACTIVITY_ACTIONS/COMPLETE', () => ({
    request: {
      url: '/protrack-levels-progress',
      method: 'POST',
      data: {
        actionType: COMMIT,
        completePreviousActivity: true,
      },
    },
  })),
}

export const sendYesterdayActivity = request(
  'IDENTITY/YESTERDAY_ACTIVITY/DID',
  ({ activityId, userActivityId, actionType, actionSource = 'web' }) => ({
    request: {
      url: '/protrack-levels-progress',
      method: 'POST',
      data: { activityId, userActivityId, actionType, actionSource },
    },
  })
)

export const setProtrackRating = request('IDENTITY/SET_PROTRACK_RATING', values => ({
  request: {
    url: '/rates/protracks-levels',
    method: 'POST',
    data: values,
  },
}))

export const requestSurvey = request('IDENTITY/SEND_SKIP_SURVEY_EMAIL', ({ surveyType, sendEmail }) => ({
  request: {
    url: '/protrack-levels-progress/request-survey',
    method: 'POST',
    data: { surveyType, sendEmail },
  },
}))

export const setOneSignalId = request('IDENTITY/SET_ONE_SIGNAL_ID', identifier => ({
  request: {
    url: '/me/one-signal',
    method: 'POST',
    data: { identifier },
  },
}))

export const removeOneSignalId = request('IDENTITY/REMOVE_ONE_SIGNAL_ID', identifier => ({
  request: {
    url: '/me/one-signal',
    method: 'DELETE',
    data: { identifier },
  },
}))

export const setAuthEmailStatus = createAction('IDENTITY/SET_AUTH_EMAIL_STATUS', status => ({ status }))

export const getActivitiesHistory = request('IDENTITY/GET_ACTIVITIES_HISTORY', ({ page, limit }) => ({
  request: {
    url: `/protrack-levels-progress/history/?page=${page || 0}&limit=${limit || 30}`,
    method: 'GET',
  },
}))

export const setFeatureAsShown = request('IDENTITY/SET_FEATURE_AS_SHOWN', data => ({
  request: {
    url: '/me/feature',
    method: 'PUT',
    data,
  },
}))

export const setFeatureTooltipAsShown = createAction(
  'IDENTITY/SET_FEATURE_TOOLTIP_AS_SHOWN',
  tooltipName => tooltipName
)

export const markLoginFlowAsFinished = request('IDENTITY/MARK_LOGIN_FLOW_AS_FINISHED', () => ({
  request: {
    url: '/me/login-flow',
    method: 'PUT',
  },
}))

export const setYesterdayStory = request(
  'ACTIVITY/SET_YESTERDAY_STORY',
  ({ activityId, story, mentions, userActivityId }) => ({
    request: {
      url: '/activities-users-stories',
      method: 'POST',
      data: {
        activityId,
        story,
        mentions,
        userActivityId,
      },
    },
  })
)

export const setActivityRating = request('ACTIVITY/SET_ACTIVITY_RATING', data => ({
  request: {
    url: `/rates/activities`,
    method: 'POST',
    data,
  },
}))

export const setCustomStory = request(
  'ACTIVITY/SET_CUSTOM_STORY',
  ({ activityId, userActivityId, story, mentions }) => ({
    request: {
      url: '/activities-users-stories',
      method: 'POST',
      data: {
        activityId,
        userActivityId,
        story,
        mentions,
      },
    },
  })
)

export const transferUserToOtherOrg = request('IDENTITY/TRANSFER_USER', data => ({
  request: {
    url: '/users/transfer',
    method: 'POST',
    data: data.toJS ? data.toJS() : data,
  },
}))
